import { Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  InvitationsService,
  UserInvitation,
  UserInvitationRequest,
} from 'src/app/services/invitations.service';
import {
  ApiInvoice,
  DesktopInvoiceDetails,
  InvoiceStatus,
  InvoicesService,
} from 'src/app/services/invoices.service';
import {
  ActivationKey,
  DesktopLicenseDetails,
  DesktopLicenseWithProduct,
  LicensesService,
} from 'src/app/services/licenses.service';
import { Role, RolesService } from 'src/app/services/roles.service';
import { Tenant, TenantsService } from 'src/app/services/tenants.service';
import {
  UserWithPermissions,
  UsersService,
} from 'src/app/services/users.service';
import {
  Franchise,
  FranchisesService,
} from 'src/app/services/franchises.service';
import { AuthService } from 'src/app/services/auth.service';
import { HttpErrorResponse } from '@angular/common/http';
import { ToastService } from 'src/app/services/toast.service';
import {
  ApiSubscription,
  ApiSubscriptionResponse,
  ApiSubscriptionsService,
} from 'src/app/services/api-subscriptions.service';
import {
  AuthorizeNetSavedPaymentMethod,
  PaymentsService,
} from 'src/app/services/payments.service';
import { AddPaymentMethodModalComponent } from 'src/app/components/saved-payment-methods/add-payment-method-modal/add-payment-method-modal.component';
import { RemovePaymentMethodModalComponent } from 'src/app/components/saved-payment-methods/remove-payment-method-modal/remove-payment-method-modal.component';
import { EmailActivationKeysComponent } from './email-activation-keys/email-activation-keys.component';
import { SentActivationKeyEmailsComponent } from './sent-activation-key-emails/sent-activation-key-emails.component';
import {
  DESCreditRecord,
  DesktopLicenseRecords,
  MachinesService,
  NewDesktopLicenseRecord,
  RegistrationRecord,
} from 'src/app/services/machines.service';
import {
  NewLicenseProductCatalog,
  RegistrationDbService,
} from 'src/app/services/registration-db.service';
import { ErrorService } from 'src/app/services/error.service';
import { DesCreditsUpdatedKind } from './tenant-des-credits/tenant-des-credits.component';

type LicenseGroups = {
  active: DesktopLicenseDetails[];
  expired: DesktopLicenseDetails[];
  pending: DesktopLicenseDetails[];
  disabled: DesktopLicenseDetails[];
};

const LICENSE_GROUP_KEYS: (keyof LicenseGroups)[] = [
  'active',
  'pending',
  'expired',
  'disabled',
];

type Sort = {
  field: string;
  direction: 'ASC' | 'DESC';
};

type TenantDetailsTab =
  | 'desktop'
  | 'api'
  | 'des-credits'
  | 'users'
  | 'payment-methods';

@Component({
  selector: 'app-tenant-details',
  templateUrl: './tenant-details.component.html',
  styleUrls: ['./tenant-details.component.scss'],
})
export class TenantDetailsComponent implements OnDestroy {
  @ViewChild('addLicenseDialog', { static: true })
  addLicenseDialog!: ElementRef<HTMLDialogElement>;
  @ViewChild('emailActivationKeys')
  emailActivationKeys!: EmailActivationKeysComponent;
  @ViewChild('sentActivationKeyEmails')
  sentActivationKeyEmails!: SentActivationKeyEmailsComponent;
  @ViewChild('assignInitialUniqueIDDialog', { static: true })
  assignInitialUniqueIDDialog!: ElementRef<HTMLDialogElement>;
  @ViewChild('createInvoiceDialog', { static: true })
  createInvoiceDialog!: ElementRef<HTMLDialogElement>;
  @ViewChild('addApiSubscriptionDialog', { static: true })
  addApiSubscriptionDialog!: ElementRef<HTMLDialogElement>;
  @ViewChild('inviteUserModal') inviteUserModal!: ElementRef<HTMLDialogElement>;
  @ViewChild('productSearchInput')
  productSearchInput!: ElementRef<HTMLInputElement>;
  @ViewChild('deleteInvoiceDialog')
  deleteInvoiceDialog!: ElementRef<HTMLDialogElement>;
  @ViewChild('deleteApiInvoiceDialog')
  deleteApiInvoiceDialog!: ElementRef<HTMLDialogElement>;
  @ViewChild('registrationRecordConfirmDialog')
  registrationRecordConfirmDialog!: ElementRef<HTMLDialogElement>;
  @ViewChild('deleteNewLicenseConfirmDialog')
  deleteNewLicenseConfirmDialog!: ElementRef<HTMLDialogElement>;
  @ViewChild('createNewLicenseDialog')
  createNewLicenseDialog!: ElementRef<HTMLDialogElement>;
  @ViewChild('changeStatusDialog')
  changeStatusDialog!: ElementRef<HTMLDialogElement>;
  @ViewChild('changeApiInvoiceStatusDialog')
  changeApiInvoiceStatusDialog!: ElementRef<HTMLDialogElement>;
  @ViewChild('addPaymentMethodModal')
  addPaymentMethodModal!: AddPaymentMethodModalComponent;
  @ViewChild('removePaymentMethodModal')
  removePaymentMethodModal!: RemovePaymentMethodModalComponent;

  tab: TenantDetailsTab = 'desktop';

  tenant?: Tenant;
  franchise?: Franchise;
  desktopLicenses?: LicenseGroups;
  originalDesktopLicenses?: LicenseGroups;
  desktopInvoices?: {
    pending: DesktopInvoiceDetails[];
    complete: DesktopInvoiceDetails[];
    expired: DesktopInvoiceDetails[];
  };
  apiInvoices?: {
    pending: ApiInvoice[];
    complete: ApiInvoice[];
    expired: ApiInvoice[];
  };
  users?: UserWithPermissions[];
  invitations?: {
    pending: UserInvitation[];
    accepted: UserInvitation[];
    expired: UserInvitation[];
    revoked: UserInvitation[];
  };
  apiSubscription?: ApiSubscriptionResponse;
  savedPaymentMethods?: AuthorizeNetSavedPaymentMethod[];
  methodToRemove?: AuthorizeNetSavedPaymentMethod;
  settingDefaultMethod = false;

  loadingUsers = false;
  loadingInvitations = false;
  loadingTenant = false;
  loadingFranchise = false;
  loadingLicenses = false;
  loadingInvoices = false;
  loadingApiInvoices = false;
  loadingApiSubscription = false;
  loadingPaymentMethods = false;

  error = '';
  errorFetchingTenant = '';
  errorFetchingFranchise = '';
  errorFetchingLicenses = '';
  errorFetchingInvoices = '';
  errorFetchingApiInvoices = '';
  errorFetchingUsers = '';
  errorFetchingInvitations = '';
  errorFetchingRoles = '';
  errorFetchingApiSubscription = '';
  errorFetchingPaymentMethods = '';

  roles?: Role[];
  selectedRoleId = '';
  invitationEmail = '';
  successSendingInvitation = false;
  sendingInvitation = false;
  errorSendingInvitation = '';

  invoiceToDelete?: DesktopInvoiceDetails;
  deletingInvoice = false;
  errorDeletingInvoice = '';

  apiInvoiceToDelete?: ApiInvoice;
  deletingApiInvoice = false;
  errorDeletingApiInvoice = '';

  invoiceToChangeStatus?: DesktopInvoiceDetails;
  newInvoiceStatus: InvoiceStatus = null;
  changingStatus = false;
  errorChangingStatus = '';

  apiInvoiceToChangeStatus?: ApiInvoice;
  newApiInvoiceStatus: InvoiceStatus = null;
  changingApiInvoiceStatus = false;
  errorChangingApiInvoiceStatus = '';

  currentLicenseSort?: Sort;
  currentInvoiceSort?: Sort;
  currentApiInvoiceSort?: Sort;
  currentUserSort?: Sort;

  initialUniqueIDs: { [key: string]: string } = {};
  applyingInitialUniqueIds = false;
  machines?: DesktopLicenseRecords;
  distinctActiveMachines: RegistrationRecord[] = [];
  loadingMachines = false;
  errorFetchingMachines = '';

  copyFeedbackRegistrationId: number | null = null;
  private copyFeedbackClearTimer?: ReturnType<typeof setTimeout>;

  registrationDbRecords?: DesktopLicenseRecords;
  loadingRegistrationDb = false;
  errorRegistrationDb = '';
  registrationDbFilter = '';
  registrationDbSortField: 'lastused' | 'uniqueid' | 'computername' | 'active' =
    'lastused';
  registrationDbSortDir: 'asc' | 'desc' = 'desc';
  registrationDbBusyId: number | null = null;
  registrationRecordPendingConfirm: {
    rec: RegistrationRecord;
    action: 'activate' | 'deactivate';
  } | null = null;
  newLicenseRecordPendingDelete: NewDesktopLicenseRecord | null = null;

  newLicenseProductCatalog: NewLicenseProductCatalog | null = null;
  loadingNewLicenseProducts = false;
  creatingNewLicense = false;
  newLicensePrimaryCode = '';
  newLicenseAddonCodes: Record<string, boolean> = {};
  newLicenseManifestCodes: Record<string, boolean> = {};
  newLicenseExpirationDate = '';
  newLicenseCreateError = '';

  errorRevokingInvitation = '';

  showCreateInvoice = false;

  regCollapsed = false;
  invoicesCollapsed = false;
  licensesCollapsed = false;

  constructor(
    private authService: AuthService,
    private apiSubscriptionsService: ApiSubscriptionsService,
    private errorService: ErrorService,
    private franchisesService: FranchisesService,
    private invitationsService: InvitationsService,
    private invoicesService: InvoicesService,
    private licensesService: LicensesService,
    private machinesService: MachinesService,
    private route: ActivatedRoute,
    private router: Router,
    private rolesService: RolesService,
    private tenantsService: TenantsService,
    private usersService: UsersService,
    private toastService: ToastService,
    private paymentsService: PaymentsService,
    private registrationDbService: RegistrationDbService
  ) {}

  isPortalAdmin(): boolean {
    return this.authService.isAdmin();
  }

  ngOnDestroy(): void {
    this.clearCopyFeedback();
  }

  ngOnInit(): void {
    console.log('ProductDetailsComponent ngOnInit');
    const tenantId = this.route.snapshot.paramMap.get('id');

    if (tenantId === null || tenantId === '') {
      console.error(new Error('tenant id not provided in route params'));
      this.error = 'Tenant ID not provided in route params';
      return;
    }

    this.route.queryParams.subscribe((params) => {
      const tab = params['tab'];
      if (
        tab === 'desktop' ||
        tab === 'api' ||
        tab === 'des-credits' ||
        tab === 'users' ||
        tab === 'payment-methods'
      ) {
        this.tab = tab;
      }
    });

    this.getTenantDetails(tenantId);
    this.getLicensesForTenant(tenantId);
    this.getApiSubscriptionForTenant(tenantId);
    this.getInvoicesForTenant(tenantId);
    this.getApiInvoicesForTenant(tenantId);
    this.getUsersForTenant(tenantId);
    this.getUserInvitationsForTenant(tenantId);
    this.getRoles();
    this.getPaymentMethodsForTenant(tenantId);
  }

  switchTab(tab: TenantDetailsTab) {
    this.tab = tab;
    // add tab to query params
    this.router.navigate([], {
      queryParams: { tab: tab },
    });
  }

  getTenantDetails(tenantId: string) {
    this.loadingTenant = true;
    this.tenantsService.getTenantById(tenantId).subscribe({
      next: (tenant) => {
        this.tenant = tenant.tenant;
        this.loadRegistrationDbRecords();
        if (tenant.tenant.franchise_id) {
          this.getFranchiseDetails(tenant.tenant.franchise_id);
        } else {
          this.loadingTenant = false;
        }
      },
      error: (err) => {
        console.error(err);
        this.loadingTenant = false;
        this.errorFetchingTenant = err;
      },
    });
  }

  getFranchiseDetails(franchiseId: string) {
    this.loadingFranchise = true;
    this.franchisesService.getFranchiseById(franchiseId).subscribe({
      next: (franchise) => {
        this.loadingFranchise = false;
        this.loadingTenant = false;
        this.franchise = franchise.franchise;
      },
      error: (err) => {
        this.loadingFranchise = false;
        this.loadingTenant = false;
        console.error(err);
        this.errorFetchingFranchise = err;
      },
    });
  }

  getLicensesForTenant(tenantId: string) {
    this.loadingLicenses = true;
    this.licensesService.getLicensesForTenant(tenantId, true).subscribe({
      next: (licenses) => {
        console.log('licenses: ', licenses);
        this.loadingLicenses = false;
        const sortedLicenses = this.sortLicensesByDefault(
          licenses.desktop_licenses
        );
        this.desktopLicenses = sortedLicenses;
        this.originalDesktopLicenses = JSON.parse(
          JSON.stringify(sortedLicenses)
        );
      },
      error: (err) => {
        console.error(err);
        this.loadingLicenses = false;
        this.errorFetchingLicenses = err;
      },
    });
  }

  getApiSubscriptionForTenant(tenantId: string) {
    this.loadingApiSubscription = true;
    this.apiSubscriptionsService
      .getApiSubscriptionByTenantId(tenantId)
      .subscribe({
        next: (apiSubscription) => {
          this.loadingApiSubscription = false;
          if (apiSubscription) {
            this.apiSubscription = apiSubscription;
          } else {
            this.apiSubscription = undefined;
          }
        },
        error: (err) => {
          console.error(err);
          this.loadingApiSubscription = false;
          this.errorFetchingApiSubscription = err;
        },
      });
  }

  getMachinesForTenant(tenantId: string) {
    this.loadingMachines = true;
    this.machines = undefined;
    this.distinctActiveMachines = [];
    this.errorFetchingMachines = '';
    this.machinesService.getMachinesForTenant(tenantId).subscribe({
      next: (machines) => {
        this.loadingMachines = false;
        this.machines = machines;
        this.distinctActiveMachines = this.getDistinctActiveMachines(machines);
      },
      error: (err) => {
        console.error(err);
        this.loadingMachines = false;
        this.distinctActiveMachines = [];
        this.errorFetchingMachines = err;
      },
    });
  }

  private parseLastUsedMs(lastused?: string): number {
    if (!lastused) {
      return 0;
    }
    const t = Date.parse(lastused);
    return Number.isNaN(t) ? 0 : t;
  }

  /** Active registrations only; one row per uniqueid (latest lastused wins). */
  private getDistinctActiveMachines(
    records: DesktopLicenseRecords
  ): RegistrationRecord[] {
    const list = records.active ?? [];
    const byUnique = new Map<string, RegistrationRecord>();
    for (const m of list) {
      const uid = m.uniqueid?.trim();
      if (!uid) {
        continue;
      }
      const existing = byUnique.get(uid);
      if (!existing) {
        byUnique.set(uid, m);
        continue;
      }
      if (
        this.parseLastUsedMs(m.lastused) >=
        this.parseLastUsedMs(existing.lastused)
      ) {
        byUnique.set(uid, m);
      }
    }
    return Array.from(byUnique.values()).sort(
      (a, b) =>
        this.parseLastUsedMs(b.lastused) - this.parseLastUsedMs(a.lastused)
    );
  }

  private clearCopyFeedback(): void {
    if (this.copyFeedbackClearTimer !== undefined) {
      clearTimeout(this.copyFeedbackClearTimer);
      this.copyFeedbackClearTimer = undefined;
    }
    this.copyFeedbackRegistrationId = null;
  }

  copyMachineUniqueId(uniqueId: string | undefined, registrationId: number) {
    const id = uniqueId?.trim();
    if (!id) {
      return;
    }
    navigator.clipboard.writeText(id).then(() => {
      if (this.copyFeedbackClearTimer !== undefined) {
        clearTimeout(this.copyFeedbackClearTimer);
      }
      this.copyFeedbackRegistrationId = registrationId;
      this.copyFeedbackClearTimer = setTimeout(() => {
        this.copyFeedbackRegistrationId = null;
        this.copyFeedbackClearTimer = undefined;
      }, 2000);
    });
  }

  pasteUniqueIdIntoLicense(license: DesktopLicenseDetails) {
    navigator.clipboard.readText().then(
      (text) => {
        const t = text.trim();
        this.initialUniqueIDs = {
          ...this.initialUniqueIDs,
          [license.id]: t,
        };
      },
      () => {
        this.toastService.addToast('error', 'Could not read clipboard');
      }
    );
  }

  getLicenseType(license: DesktopLicenseDetails) {
    return this.licensesService.getType(license);
  }

  navigateToApiJobLogs() {
    const tenantId = this.tenant!.id;
    this.router.navigate(['/admin/tenant/' + tenantId + '/api-job-logs']);
  }

  tenantAddressValid(tenant: Tenant) {
    return this.tenantsService.tenantAddressValid(tenant);
  }

  sortLicensesByDefault(licenseGroups: LicenseGroups) {
    this.currentLicenseSort = {
      field: 'default',
      direction: 'ASC',
    };

    for (const group in licenseGroups) {
      const licenses = licenseGroups[group as keyof LicenseGroups];

      const sortedLicenses =
        this.licensesService.sortLicensesByRelationship(licenses);
      console.log(`${group} sortedLicenses: `, sortedLicenses);

      const sortedDetails = [];
      for (const license of sortedLicenses) {
        const details = licenses.find((l) => l.id === license.id);
        if (details) {
          sortedDetails.push(details);
        }
      }

      licenseGroups[group as keyof LicenseGroups] = sortedDetails;
    }

    return licenseGroups;
  }

  sortLicenses(field: string) {
    let direction: 'ASC' | 'DESC' = 'ASC';
    if (
      this.currentLicenseSort?.field === field &&
      this.currentLicenseSort?.direction === 'ASC'
    ) {
      direction = 'DESC';
    }
    this.currentLicenseSort = {
      field: field,
      direction: direction,
    };

    if (field === 'default') {
      this.desktopLicenses = JSON.parse(
        JSON.stringify(this.originalDesktopLicenses)
      );
      return;
    }

    this.desktopLicenses?.active.sort((a, b) =>
      this.compFunc(a, b, field, this.currentLicenseSort!.direction)
    );
    this.desktopLicenses?.expired.sort((a, b) =>
      this.compFunc(a, b, field, this.currentLicenseSort!.direction)
    );
    this.desktopLicenses?.pending.sort((a, b) =>
      this.compFunc(a, b, field, this.currentLicenseSort!.direction)
    );
    this.desktopLicenses?.disabled.sort((a, b) =>
      this.compFunc(a, b, field, this.currentLicenseSort!.direction)
    );
  }

  sortInvoices(field: string) {
    let direction: 'ASC' | 'DESC' = 'DESC';
    if (
      this.currentInvoiceSort?.field === field &&
      this.currentInvoiceSort?.direction === 'DESC'
    ) {
      direction = 'ASC';
    }
    this.currentInvoiceSort = {
      field: field,
      direction: direction,
    };

    this.desktopInvoices?.pending.sort((a, b) =>
      this.compFunc(a, b, field, direction)
    );
    this.desktopInvoices?.complete.sort((a, b) =>
      this.compFunc(a, b, field, direction)
    );
    this.desktopInvoices?.expired.sort((a, b) =>
      this.compFunc(a, b, field, direction)
    );
  }

  sortApiInvoices(field: string) {
    let direction: 'ASC' | 'DESC' = 'DESC';
    if (
      this.currentApiInvoiceSort?.field === field &&
      this.currentApiInvoiceSort?.direction === 'DESC'
    ) {
      direction = 'ASC';
    }
    this.currentApiInvoiceSort = {
      field: field,
      direction: direction,
    };

    this.apiInvoices?.pending.sort((a, b) =>
      this.compFunc(a, b, field, direction)
    );
    this.apiInvoices?.complete.sort((a, b) =>
      this.compFunc(a, b, field, direction)
    );
    this.apiInvoices?.expired.sort((a, b) =>
      this.compFunc(a, b, field, direction)
    );
  }

  sortUsers(field: string) {
    let direction: 'ASC' | 'DESC' = 'DESC';
    if (
      this.currentUserSort?.field === field &&
      this.currentUserSort?.direction === 'DESC'
    ) {
      direction = 'ASC';
    }
    this.currentUserSort = {
      field: field,
      direction: direction,
    };

    this.users?.sort((a, b) => this.compFunc(a, b, field, direction));
  }

  compFunc(a: any, b: any, field: string, order: 'ASC' | 'DESC') {
    const aVal = field.split('.').reduce((p, c) => p?.[c], a);
    const bVal = field.split('.').reduce((p, c) => p?.[c], b);
    if (aVal && bVal) {
      const diff = aVal > bVal ? 1 : -1;
      return order === 'ASC' ? diff : -diff;
    }
    if (aVal) {
      return order === 'ASC' ? 1 : -1;
    }
    if (bVal) {
      return order === 'ASC' ? -1 : 1;
    }
    return 0;
  }

  getInvoicesForTenant(tenantId: string) {
    this.loadingInvoices = true;
    this.invoicesService.getInvoicesForTenant(tenantId).subscribe({
      next: (invoices) => {
        console.log('invoices: ', invoices);
        this.loadingInvoices = false;
        this.desktopInvoices = invoices.desktop_invoices;
      },
      error: (err) => {
        console.error(err);
        this.loadingInvoices = false;
        this.errorFetchingInvoices = err;
      },
    });
  }

  getApiInvoicesForTenant(tenantId: string) {
    this.loadingApiInvoices = true;
    this.invoicesService.getApiInvoicesForTenant(tenantId).subscribe({
      next: (res) => {
        this.loadingApiInvoices = false;
        console.log('api invoices: ', res);
        this.apiInvoices = res.api_invoices;
      },
      error: (err) => {
        console.error(err);
        this.loadingApiInvoices = false;
        this.errorFetchingApiInvoices = err;
      },
    });
  }

  getUsersForTenant(tenantId: string) {
    this.loadingUsers = true;
    this.usersService.getUsersForTenant(tenantId).subscribe({
      next: (users) => {
        this.loadingUsers = false;
        this.users = users.users;
      },
      error: (err) => {
        console.error(err);
        this.loadingUsers = false;
        this.errorFetchingUsers = err;
      },
    });
  }

  getUserInvitationsForTenant(tenantId: string) {
    this.loadingInvitations = true;
    this.invitationsService.getInvitationsForTenant(tenantId).subscribe({
      next: (res) => {
        this.loadingInvitations = false;
        this.invitations = res.user_invitations;
      },
      error: (err) => {
        console.error(err);
        this.loadingInvitations = false;
        this.errorFetchingInvitations = err;
      },
    });
  }

  getRoles() {
    this.rolesService.getRolesWithAccess().subscribe({
      next: (res) => {
        this.roles = res.roles;
      },
      error: (err) => {
        this.errorFetchingRoles = err;
      },
    });
  }

  roleList(user: UserWithPermissions) {
    return user.permissions.map((p) => p.role.display_name).join(', ');
  }

  showAddLicenseTable() {
    this.addLicenseDialog?.nativeElement.showModal();
  }

  handleLicensesAdded(licenses: DesktopLicenseWithProduct[]) {
    console.log('handleLicensesAdded: ', licenses);

    this.getLicensesForTenant(this.tenant!.id);

    this.addLicenseDialog?.nativeElement.close();
  }

  handleLicenseTransferred(license: DesktopLicenseDetails) {
    console.log('license transferred: ', license);
    this.upsertLicenseInLists(license);
  }

  private upsertLicenseInLists(license: DesktopLicenseDetails) {
    const patchGroups = (groups?: LicenseGroups): LicenseGroups | undefined => {
      if (!groups) {
        return groups;
      }
      const next: LicenseGroups = { ...groups };
      for (const group of LICENSE_GROUP_KEYS) {
        const idx = next[group].findIndex((l) => l.id === license.id);
        if (idx === -1) {
          continue;
        }
        const updated = [...next[group]];
        updated[idx] = license;
        next[group] = updated;
        break;
      }
      return next;
    };

    this.desktopLicenses = patchGroups(this.desktopLicenses);
    this.originalDesktopLicenses = patchGroups(this.originalDesktopLicenses);
  }

  private applyActivationKeysToLicenses(keys: ActivationKey[]) {
    if (!this.desktopLicenses || keys.length === 0) {
      return;
    }
    const byLicenseId = new Map(keys.map((k) => [k.desktop_license_id, k]));
    for (const group of LICENSE_GROUP_KEYS) {
      for (const license of this.desktopLicenses[group]) {
        const key = byLicenseId.get(license.id);
        if (!key) {
          continue;
        }
        this.upsertLicenseInLists({
          ...license,
          unique_id: key.unique_id,
          activation_key: key,
        });
      }
    }
  }

  showEmailActivationKeysDialog() {
    this.emailActivationKeys.openDialog();
  }

  onActivationKeysEmailed() {
    this.sentActivationKeyEmails?.load();
  }

  get licensesWithoutUniqueIDs(): DesktopLicenseDetails[] {
    if (!this.desktopLicenses) {
      return [];
    }

    return [
      ...this.desktopLicenses?.active.filter(
        (l) => !l.activation_key?.unique_id
      ),
      ...this.desktopLicenses.pending.filter(
        (l) => !l.activation_key?.unique_id
      ),
    ];
  }

  showInitialUniqueIDDialog() {
    // reset the initial unique ids
    this.initialUniqueIDs = {};
    this.clearCopyFeedback();
    this.getMachinesForTenant(this.tenant!.id);
    this.assignInitialUniqueIDDialog.nativeElement.showModal();
  }

  closeAssignInitialUniqueIDDialog() {
    this.initialUniqueIDs = {};
    this.applyingInitialUniqueIds = false;
    this.distinctActiveMachines = [];
    this.clearCopyFeedback();
    this.assignInitialUniqueIDDialog.nativeElement.close();
  }

  handleInitialUniqueIDChange(value: string, license: DesktopLicenseDetails) {
    this.initialUniqueIDs = {
      ...this.initialUniqueIDs,
      [license.id]: value,
    };
  }

  applyInitialUniqueIDs() {
    if (!this.tenant) {
      return;
    }
    const updates = Object.entries(this.initialUniqueIDs)
      .filter(([, uid]) => uid?.trim())
      .map(([license_id, unique_id]) => ({
        license_id,
        unique_id: unique_id.trim(),
      }));
    if (updates.length === 0) {
      this.toastService.addToast(
        'error',
        'Enter at least one Unique ID for a license row.'
      );
      return;
    }
    this.applyingInitialUniqueIds = true;
    this.licensesService
      .applyInitialUniqueIds({
        tenant_id: this.tenant.id,
        generate_activation_keys: true,
        updates,
      })
      .subscribe({
        next: (response) => {
          this.applyingInitialUniqueIds = false;
          this.toastService.addToast('success', 'Unique IDs applied.');
          this.closeAssignInitialUniqueIDDialog();
          if (response.activation_keys?.length) {
            this.applyActivationKeysToLicenses(response.activation_keys);
          } else {
            this.getLicensesForTenant(this.tenant!.id);
          }
        },
        error: (err: unknown) => {
          console.error(err);
          this.applyingInitialUniqueIds = false;
          const errorMessage = this.errorService.getErrorMessage(err);
          this.toastService.addToast(
            'error',
            errorMessage || 'Could not apply Unique IDs.'
          );
        },
      });
  }

  hasPendingInvoice() {
    return this.desktopInvoices?.pending?.length ?? 0 > 0;
  }

  hasNoLicenses() {
    if (
      this.desktopLicenses?.active.length === 0 &&
      this.desktopLicenses.disabled.length === 0 &&
      this.desktopLicenses.expired.length === 0 &&
      this.desktopLicenses.pending.length === 0
    ) {
      return true;
    }
    return false;
  }

  showCreateInvoiceModal() {
    this.createInvoiceDialog.nativeElement.showModal();
    this.showCreateInvoice = true;
  }

  closeCreateInvoiceDialog() {
    this.createInvoiceDialog.nativeElement.close();
    setTimeout(() => {
      this.showCreateInvoice = false;
    }, 500);
  }

  handleInvoiceCreated(invoice: DesktopInvoiceDetails) {
    console.log('invoice created: ', invoice);
    this.toastService.addToast('success', 'Invoice created successfully');

    if (this.desktopInvoices) {
      this.desktopInvoices.pending.push(invoice);
      // assign new object to force change detection
      this.desktopInvoices = Object.assign({}, this.desktopInvoices);
    } else {
      this.desktopInvoices = {
        pending: [invoice],
        complete: [],
        expired: [],
      };
    }

    // trigger a license fetch to update the invoice indicators
    this.getLicensesForTenant(this.tenant!.id);

    this.createInvoiceDialog.nativeElement.close();
  }

  showAddApiSubscriptionModal() {
    this.addApiSubscriptionDialog.nativeElement.showModal();
  }

  handleApiSubscriptionAdded(apiSubscription: ApiSubscription) {
    console.log('api subscription added: ', apiSubscription);
    this.toastService.addToast(
      'success',
      'API subscription added successfully'
    );
    this.getApiSubscriptionForTenant(this.tenant!.id);
    this.addApiSubscriptionDialog.nativeElement.close();
  }

  browseToInvoice(invoiceId: string, tenantId: string) {
    window.location.href =
      '/admin/tenant/' + tenantId + '/invoice/' + invoiceId;
  }

  promptDeleteInvoice(invoice: DesktopInvoiceDetails) {
    this.invoiceToDelete = invoice;
    this.deleteInvoiceDialog.nativeElement.showModal();
  }

  closeDeleteInvoiceDialog() {
    this.deleteInvoiceDialog.nativeElement.close();
    this.invoiceToDelete = undefined;
  }

  deleteInvoice() {
    if (!this.invoiceToDelete) {
      console.error(new Error('No invoice to delete'));
      this.errorDeletingInvoice = 'No invoice to delete';
      return;
    }

    const invoiceToDelete = this.invoiceToDelete;

    this.deletingInvoice = true;
    this.invoicesService
      .deleteInvoice(invoiceToDelete.desktop_invoice.id)
      .subscribe({
        next: (res) => {
          console.log('invoice deleted: ', res);
          this.deletingInvoice = false;
          this.errorDeletingInvoice = '';
          this.invoiceToDelete = undefined;

          // remove invoice from list it was in
          if (this.desktopInvoices?.pending) {
            this.desktopInvoices.pending = this.desktopInvoices.pending.filter(
              (i) => i.desktop_invoice.id !== invoiceToDelete.desktop_invoice.id
            );
          }
          if (this.desktopInvoices?.expired) {
            this.desktopInvoices.expired = this.desktopInvoices.expired.filter(
              (i) => i.desktop_invoice.id !== invoiceToDelete.desktop_invoice.id
            );
          }

          // trigger a license fetch to update the invoice indicators
          this.getLicensesForTenant(this.tenant!.id);

          this.deleteInvoiceDialog.nativeElement.close();
          this.toastService.addToast('success', 'Invoice deleted successfully');
        },
        error: (err) => {
          this.deletingInvoice = false;
          console.error(err);
          this.errorDeletingInvoice = 'Unexpected error deleting invoice';
        },
      });
  }

  getInvitationLink(invitation: UserInvitation) {
    return window.location.origin + '/sign-up/' + invitation.id;
  }

  copyInvitationLink(invitation: UserInvitation) {
    navigator.clipboard.writeText(this.getInvitationLink(invitation));
    this.toastService.addToast('success', 'Invitation link copied!');
  }

  sendInvitation() {
    if (!this.invitations || !this.tenant) {
      this.errorSendingInvitation = 'Error sending invitation';
      return;
    }

    const request: UserInvitationRequest = {
      email: this.invitationEmail,
      starting_role_id: this.selectedRoleId,
      tenant_id: this.tenant.id,
      first_user: false,
    };

    this.successSendingInvitation = false;
    this.errorSendingInvitation = '';
    this.sendingInvitation = true;
    this.invitationsService.createInvitationForTenant(request).subscribe({
      next: (result) => {
        this.inviteUserModal.nativeElement.close();
        this.sendingInvitation = false;
        if (!this.invitations) {
          this.errorSendingInvitation = 'Error sending invitation';
          return;
        }
        this.successSendingInvitation = true;
        this.invitationEmail = '';
        this.selectedRoleId = '';
        console.log('invitation created: ', result);
        this.invitations.pending.push(result.user_invitation);
      },
      error: (err) => {
        this.inviteUserModal.nativeElement.close();
        this.sendingInvitation = false;
        if (err.error?.error === 'email already in use') {
          this.errorSendingInvitation = 'Email already in use';
        } else {
          this.errorSendingInvitation = 'Error creating invitation';
        }
        console.error(err);
      },
    });
  }

  canEditUser(userToEdit: UserWithPermissions) {
    if (!this.authService.currentUser) return false;

    return this.usersService.canEditUser(
      this.authService.currentUser,
      userToEdit
    );
  }

  revokeInvitation(invitation: UserInvitation) {
    this.invitationsService.revokeInvitation(invitation.id).subscribe({
      next: (res) => {
        if (!this.invitations) {
          this.errorFetchingInvitations = 'Invitations not loaded properly';
          return;
        }
        // remove invitation from pending list
        this.invitations.pending = this.invitations.pending.filter(
          (i) => i.id !== invitation.id
        );
        // add to revoked invitations
        this.invitations.revoked.push(res.user_invitation);

        this.toastService.addToast(
          'success',
          'Invitation revoked successfully'
        );
      },
      error: (err) => {
        console.error(err);
        if (err instanceof HttpErrorResponse) {
          this.errorRevokingInvitation = err.statusText;
        }
        this.errorRevokingInvitation = err.message || err;
      },
    });
  }

  promptChangeStatus(invoice: DesktopInvoiceDetails) {
    this.invoiceToChangeStatus = invoice;
    this.newInvoiceStatus = invoice.desktop_invoice.status || null;
    this.errorChangingStatus = '';
    this.changeStatusDialog.nativeElement.showModal();
  }

  closeChangeStatusDialog() {
    this.changeStatusDialog.nativeElement.close();
    this.invoiceToChangeStatus = undefined;
    this.newInvoiceStatus = null;
    this.errorChangingStatus = '';
  }

  updateInvoiceStatus() {
    if (!this.invoiceToChangeStatus) {
      console.error(new Error('No invoice selected'));
      this.errorChangingStatus = 'No invoice selected';
      return;
    }

    const invoiceToUpdate = this.invoiceToChangeStatus;

    this.changingStatus = true;
    this.errorChangingStatus = '';
    this.invoicesService
      .updateDesktopInvoiceStatus(
        invoiceToUpdate.desktop_invoice.id,
        this.newInvoiceStatus
      )
      .subscribe({
        next: (res) => {
          console.log('invoice status updated: ', res);
          this.changingStatus = false;
          this.errorChangingStatus = '';

          // Update the invoice in the list
          if (this.desktopInvoices?.complete) {
            const index = this.desktopInvoices.complete.findIndex(
              (i) => i.desktop_invoice.id === invoiceToUpdate.desktop_invoice.id
            );
            if (index !== -1) {
              this.desktopInvoices.complete[index].desktop_invoice.status =
                res.status;
              // assign new object to force change detection
              this.desktopInvoices.complete =
                this.desktopInvoices.complete.slice();
            }
          }

          this.invoiceToChangeStatus = undefined;
          this.newInvoiceStatus = null;
          this.changeStatusDialog.nativeElement.close();
          this.toastService.addToast(
            'success',
            'Invoice status updated successfully'
          );
        },
        error: (err) => {
          this.changingStatus = false;
          console.error(err);
          if (err instanceof HttpErrorResponse) {
            this.errorChangingStatus =
              err.error?.error ||
              err.statusText ||
              'Unexpected error updating invoice status';
          } else {
            this.errorChangingStatus =
              'Unexpected error updating invoice status';
          }
        },
      });
  }

  promptChangeApiInvoiceStatus(invoice: ApiInvoice) {
    this.apiInvoiceToChangeStatus = invoice;
    this.newApiInvoiceStatus = invoice.status ?? null;
    this.errorChangingApiInvoiceStatus = '';
    this.changeApiInvoiceStatusDialog.nativeElement.showModal();
  }

  closeChangeApiInvoiceStatusDialog() {
    this.changeApiInvoiceStatusDialog.nativeElement.close();
    this.apiInvoiceToChangeStatus = undefined;
    this.newApiInvoiceStatus = null;
    this.errorChangingApiInvoiceStatus = '';
  }

  submitApiInvoiceStatusChange() {
    if (!this.apiInvoiceToChangeStatus) {
      console.error(new Error('No API invoice selected'));
      this.errorChangingApiInvoiceStatus = 'No API invoice selected';
      return;
    }

    const invoiceToUpdate = this.apiInvoiceToChangeStatus;

    this.changingApiInvoiceStatus = true;
    this.errorChangingApiInvoiceStatus = '';
    this.invoicesService
      .updateApiInvoiceStatus(invoiceToUpdate.id, this.newApiInvoiceStatus)
      .subscribe({
        next: (res) => {
          this.changingApiInvoiceStatus = false;
          this.errorChangingApiInvoiceStatus = '';

          if (this.apiInvoices?.complete) {
            const index = this.apiInvoices.complete.findIndex(
              (i) => i.id === invoiceToUpdate.id
            );
            if (index !== -1) {
              this.apiInvoices.complete[index].status = res.status;
              this.apiInvoices.complete = this.apiInvoices.complete.slice();
            }
          }

          this.apiInvoiceToChangeStatus = undefined;
          this.newApiInvoiceStatus = null;
          this.changeApiInvoiceStatusDialog.nativeElement.close();
          this.toastService.addToast(
            'success',
            'API invoice status updated successfully'
          );
        },
        error: (err) => {
          this.changingApiInvoiceStatus = false;
          console.error(err);
          if (err instanceof HttpErrorResponse) {
            this.errorChangingApiInvoiceStatus =
              err.error?.error ||
              err.statusText ||
              'Unexpected error updating API invoice status';
          } else {
            this.errorChangingApiInvoiceStatus =
              'Unexpected error updating API invoice status';
          }
        },
      });
  }

  promptDeleteApiInvoice(invoice: ApiInvoice) {
    this.apiInvoiceToDelete = invoice;
    this.deleteApiInvoiceDialog.nativeElement.showModal();
  }

  closeDeleteApiInvoiceDialog() {
    this.deleteApiInvoiceDialog.nativeElement.close();
    this.apiInvoiceToDelete = undefined;
  }

  deleteApiInvoice() {
    if (!this.apiInvoiceToDelete) {
      console.error(new Error('No API invoice to delete'));
      this.errorDeletingApiInvoice = 'No API invoice to delete';
      return;
    }

    const invoiceToDelete = this.apiInvoiceToDelete;

    this.deletingApiInvoice = true;
    this.invoicesService.deleteApiInvoice(invoiceToDelete.id).subscribe({
      next: (res) => {
        console.log('API invoice deleted: ', res);
        this.deletingApiInvoice = false;
        this.errorDeletingApiInvoice = '';
        this.apiInvoiceToDelete = undefined;

        // remove invoice from list it was in
        if (this.apiInvoices?.pending) {
          this.apiInvoices.pending = this.apiInvoices.pending.filter(
            (i) => i.id !== invoiceToDelete.id
          );
        }
        if (this.apiInvoices?.complete) {
          this.apiInvoices.complete = this.apiInvoices.complete.filter(
            (i) => i.id !== invoiceToDelete.id
          );
        }
        if (this.apiInvoices?.expired) {
          this.apiInvoices.expired = this.apiInvoices.expired.filter(
            (i) => i.id !== invoiceToDelete.id
          );
        }

        this.deleteApiInvoiceDialog.nativeElement.close();
        this.toastService.addToast(
          'success',
          'API invoice deleted successfully'
        );
      },
      error: (err) => {
        this.deletingApiInvoice = false;
        console.error(err);
        this.errorDeletingApiInvoice = 'Unexpected error deleting API invoice';
      },
    });
  }

  getDisplayTransactionMonth(yyyymm: string): Date {
    return this.invoicesService.getDisplayTransactionMonth(yyyymm);
  }

  getPaymentMethodsForTenant(tenantId: string) {
    this.loadingPaymentMethods = true;
    this.paymentsService.getSavedPaymentMethods(tenantId).subscribe({
      next: (res) => {
        this.loadingPaymentMethods = false;
        this.savedPaymentMethods = res.payment_profiles;
      },
      error: (err) => {
        console.error(err);
        this.loadingPaymentMethods = false;
        this.errorFetchingPaymentMethods = 'Error fetching payment methods';
      },
    });
  }

  showAddPaymentMethodModal() {
    if (this.tenant) {
      this.addPaymentMethodModal.tenantId = this.tenant.id;
      this.addPaymentMethodModal.showAddMethodModal();
    }
  }

  closeAddPaymentMethodModal() {
    this.addPaymentMethodModal.hideAddMethodModal();
  }

  handlePaymentMethodCreation(paymentMethod: AuthorizeNetSavedPaymentMethod) {
    if (!this.savedPaymentMethods) {
      this.savedPaymentMethods = [];
    }
    this.savedPaymentMethods.push(paymentMethod);
    // if returned payment method is default, update all others
    if (paymentMethod.is_default) {
      this.savedPaymentMethods.forEach((p) => {
        p.is_default = p.id === paymentMethod.id;
      });
    }
    if (paymentMethod.is_api_default) {
      this.savedPaymentMethods.forEach((p) => {
        p.is_api_default = p.id === paymentMethod.id;
      });
    }
    this.addPaymentMethodModal.hideAddMethodModal();
    this.toastService.addToast('success', 'Payment method added successfully');
  }

  setDefaultPaymentMethod(paymentMethod: AuthorizeNetSavedPaymentMethod) {
    if (!this.savedPaymentMethods) {
      console.error(new Error('No saved payment methods set'));
      return;
    }

    this.settingDefaultMethod = true;
    this.paymentsService.setDefaultPaymentMethod(paymentMethod).subscribe({
      next: (res) => {
        console.log('Payment method set as default:', res);
        this.settingDefaultMethod = false;
        this.savedPaymentMethods!.forEach((p) => {
          p.is_default = p.id === paymentMethod.id;
        });
        this.toastService.addToast('success', 'Default payment method updated');
      },
      error: (err) => {
        console.error(err);
        this.settingDefaultMethod = false;
        this.toastService.addToast(
          'error',
          'Error setting default payment method'
        );
      },
    });
  }

  setApiDefaultPaymentMethod(paymentMethod: AuthorizeNetSavedPaymentMethod) {
    if (!this.savedPaymentMethods) {
      console.error(new Error('No saved payment methods set'));
      return;
    }

    this.settingDefaultMethod = true;
    this.paymentsService.setApiDefaultPaymentMethod(paymentMethod).subscribe({
      next: (res) => {
        console.log('Payment method set as API default:', res);
        this.settingDefaultMethod = false;
        this.savedPaymentMethods!.forEach((p) => {
          p.is_api_default = p.id === paymentMethod.id;
        });
        this.toastService.addToast(
          'success',
          'API default payment method updated'
        );
      },
      error: (err) => {
        console.error(err);
        this.settingDefaultMethod = false;
        this.toastService.addToast(
          'error',
          'Error setting API default payment method'
        );
      },
    });
  }

  confirmDeleteSavedMethod(paymentMethod: AuthorizeNetSavedPaymentMethod) {
    this.methodToRemove = paymentMethod;
    this.removePaymentMethodModal.showRemoveMethodModal();
  }

  savedMethodDeleted(paymentMethod: AuthorizeNetSavedPaymentMethod) {
    console.log('saved method deleted: ', paymentMethod);
    this.savedPaymentMethods = this.savedPaymentMethods!.filter(
      (p) => p.id !== paymentMethod.id
    );
    this.methodToRemove = undefined;
    this.toastService.addToast(
      'success',
      'Payment method deleted successfully'
    );
  }

  loadRegistrationDbRecords() {
    if (!this.tenant?.account_number) {
      return;
    }
    this.loadingRegistrationDb = true;
    this.errorRegistrationDb = '';
    this.machinesService.getMachinesForTenant(this.tenant.id).subscribe({
      next: (data) => {
        this.registrationDbRecords = data;
        this.loadingRegistrationDb = false;
      },
      error: (err) => {
        this.loadingRegistrationDb = false;
        console.error('registration db error: ', err);
        this.errorRegistrationDb = 'Error fetching registration database';
      },
    });
  }

  get registrationDbRows(): RegistrationRecord[] {
    const r = this.registrationDbRecords;
    if (!r) {
      return [];
    }
    return [...r.active, ...r.inactive, ...r.uncategorized];
  }

  get newDesktopLicenseRows(): NewDesktopLicenseRecord[] {
    return this.registrationDbRecords?.new_desktop_licenses ?? [];
  }

  get desCreditRecords(): DESCreditRecord[] {
    return this.registrationDbRecords?.des_credits ?? [];
  }

  onDesCreditsUpdated(kind: DesCreditsUpdatedKind = 'credit') {
    if (kind === 'link') {
      this.toastService.addToast('success', 'DES credit linked to API key');
    } else if (kind === 'unlink') {
      this.toastService.addToast('success', 'DES credit unlinked from API key');
    } else if (kind === 'create') {
      this.toastService.addToast('success', 'DES credit record created');
    } else {
      this.toastService.addToast('success', 'DES credits updated');
    }
    this.loadRegistrationDbRecords();
  }

  get filteredRegistrationDbRows(): RegistrationRecord[] {
    const rows = [...this.registrationDbRows];
    const q = this.registrationDbFilter.trim().toLowerCase();
    let filtered = rows;
    if (q) {
      filtered = rows.filter((row) => {
        const uid = (row.uniqueid || '').toLowerCase();
        const cn = (row.computername || '').toLowerCase();
        const guid = (row.guid || '').toLowerCase();
        return uid.includes(q) || cn.includes(q) || guid.includes(q);
      });
    }
    const dir = this.registrationDbSortDir === 'asc' ? 1 : -1;
    filtered.sort((a, b) => {
      switch (this.registrationDbSortField) {
        case 'uniqueid':
          return (
            dir *
            (a.uniqueid || '').localeCompare(b.uniqueid || '', undefined, {
              sensitivity: 'base',
            })
          );
        case 'computername':
          return (
            dir *
            (a.computername || '').localeCompare(
              b.computername || '',
              undefined,
              {
                sensitivity: 'base',
              }
            )
          );
        case 'active':
          // sort order is 3, 1, 2, 0
          const activeOrder = [3, 1, 2, 0];
          return (
            dir *
            (activeOrder.indexOf(a.active) - activeOrder.indexOf(b.active))
          );
        case 'lastused':
        default:
          const aDate = new Date(a.lastused || '');
          const bDate = new Date(b.lastused || '');
          return dir * (aDate.getTime() - bDate.getTime());
      }
    });
    return filtered;
  }

  registrationDbSort(
    field: 'lastused' | 'uniqueid' | 'computername' | 'active'
  ) {
    if (this.registrationDbSortField === field) {
      this.registrationDbSortDir =
        this.registrationDbSortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.registrationDbSortField = field;
      this.registrationDbSortDir = field === 'lastused' ? 'desc' : 'asc';
    }
  }

  isRegistrationActive(rec: RegistrationRecord): boolean {
    return rec.active === 1 || rec.active === 3;
  }

  promptDeactivateRegistrationRecord(rec: RegistrationRecord) {
    this.registrationRecordPendingConfirm = { rec, action: 'deactivate' };
    this.registrationRecordConfirmDialog.nativeElement.showModal();
  }

  promptActivateRegistrationRecord(rec: RegistrationRecord) {
    this.registrationRecordPendingConfirm = { rec, action: 'activate' };
    this.registrationRecordConfirmDialog.nativeElement.showModal();
  }

  closeRegistrationRecordConfirmDialog() {
    this.registrationRecordConfirmDialog.nativeElement.close();
    this.registrationRecordPendingConfirm = null;
  }

  confirmRegistrationRecordPending() {
    const pending = this.registrationRecordPendingConfirm;
    if (!pending) {
      return;
    }
    const { rec, action } = pending;
    this.registrationRecordConfirmDialog.nativeElement.close();
    this.registrationRecordPendingConfirm = null;
    if (action === 'deactivate') {
      this.deactivateRegistrationRecord(rec);
    } else {
      this.activateRegistrationRecord(rec);
    }
  }

  deactivateRegistrationRecord(rec: RegistrationRecord) {
    if (!this.tenant?.account_number) {
      return;
    }
    this.registrationDbBusyId = rec.registration_id;
    this.registrationDbService
      .deactivateRecord(this.tenant.account_number, rec.registration_id)
      .subscribe({
        next: () => {
          this.registrationDbBusyId = null;
          this.toastService.addToast(
            'success',
            'Registration record deactivated'
          );
          this.loadRegistrationDbRecords();
          this.refreshMachinesAfterRegistrationDbChange();
        },
        error: (err) => {
          this.registrationDbBusyId = null;
          this.toastService.addToast(
            'error',
            this.errorService.getErrorMessage(err)
          );
        },
      });
  }

  activateRegistrationRecord(rec: RegistrationRecord) {
    if (!this.tenant?.account_number) {
      return;
    }
    this.registrationDbBusyId = rec.registration_id;
    this.registrationDbService
      .activateRecord(this.tenant.account_number, rec.registration_id)
      .subscribe({
        next: () => {
          this.registrationDbBusyId = null;
          this.toastService.addToast(
            'success',
            'Registration record activated'
          );
          this.loadRegistrationDbRecords();
          this.refreshMachinesAfterRegistrationDbChange();
        },
        error: (err) => {
          this.registrationDbBusyId = null;
          this.toastService.addToast(
            'error',
            this.errorService.getErrorMessage(err)
          );
        },
      });
  }

  private refreshMachinesAfterRegistrationDbChange() {
    if (this.tenant?.id) {
      this.getMachinesForTenant(this.tenant.id);
    }
  }

  promptDeleteNewLicenseRecord(rec: NewDesktopLicenseRecord) {
    this.newLicenseRecordPendingDelete = rec;
    this.deleteNewLicenseConfirmDialog.nativeElement.showModal();
  }

  closeDeleteNewLicenseConfirmDialog() {
    this.deleteNewLicenseConfirmDialog.nativeElement.close();
    this.newLicenseRecordPendingDelete = null;
  }

  confirmDeleteNewLicenseRecord() {
    const rec = this.newLicenseRecordPendingDelete;
    if (!rec || !this.tenant?.account_number) {
      return;
    }
    this.deleteNewLicenseConfirmDialog.nativeElement.close();
    this.newLicenseRecordPendingDelete = null;
    this.registrationDbBusyId = rec.registration_id;
    this.registrationDbService
      .deleteNewDesktopLicenseRecord(
        this.tenant.account_number,
        rec.registration_id
      )
      .subscribe({
        next: () => {
          this.registrationDbBusyId = null;
          this.toastService.addToast(
            'success',
            'New desktop license record deleted'
          );
          this.loadRegistrationDbRecords();
        },
        error: (err) => {
          this.registrationDbBusyId = null;
          this.toastService.addToast(
            'error',
            this.errorService.getErrorMessage(err)
          );
        },
      });
  }

  openCreateNewLicenseDialog() {
    this.newLicensePrimaryCode = '';
    this.newLicenseAddonCodes = {};
    this.newLicenseManifestCodes = {};
    this.newLicenseExpirationDate = '';
    this.newLicenseCreateError = '';
    this.newLicenseProductCatalog = null;
    this.loadingNewLicenseProducts = true;
    this.createNewLicenseDialog.nativeElement.showModal();
    this.registrationDbService.getNewDesktopLicenseProductList().subscribe({
      next: (catalog) => {
        this.newLicenseProductCatalog = catalog;
        this.loadingNewLicenseProducts = false;
        for (const p of catalog.addons ?? []) {
          this.newLicenseAddonCodes[p.code] = false;
        }
        for (const p of catalog.manifests ?? []) {
          this.newLicenseManifestCodes[p.code] = false;
        }
      },
      error: (err) => {
        this.loadingNewLicenseProducts = false;
        this.newLicenseCreateError = this.errorService.getErrorMessage(err);
      },
    });
  }

  closeCreateNewLicenseDialog() {
    this.createNewLicenseDialog.nativeElement.close();
    this.newLicenseCreateError = '';
    this.creatingNewLicense = false;
  }

  toggleNewLicenseAddon(code: string, checked: boolean) {
    this.newLicenseAddonCodes[code] = checked;
  }

  toggleNewLicenseManifest(code: string, checked: boolean) {
    this.newLicenseManifestCodes[code] = checked;
  }

  get selectedNewLicenseAddonCodes(): string[] {
    return Object.entries(this.newLicenseAddonCodes)
      .filter(([, selected]) => selected)
      .map(([code]) => code);
  }

  get selectedNewLicenseManifestCodes(): string[] {
    return Object.entries(this.newLicenseManifestCodes)
      .filter(([, selected]) => selected)
      .map(([code]) => code);
  }

  get canSubmitNewLicense(): boolean {
    return (
      !!this.newLicensePrimaryCode &&
      !!this.newLicenseExpirationDate &&
      !this.creatingNewLicense &&
      !this.loadingNewLicenseProducts
    );
  }

  submitNewLicense() {
    if (!this.tenant?.account_number || !this.canSubmitNewLicense) {
      return;
    }
    this.creatingNewLicense = true;
    this.newLicenseCreateError = '';
    this.registrationDbService
      .createNewDesktopLicense({
        account: this.tenant.account_number,
        primary_code: this.newLicensePrimaryCode,
        addon_codes: this.selectedNewLicenseAddonCodes,
        manifest_codes: this.selectedNewLicenseManifestCodes,
        expiration_date: this.newLicenseExpirationDate,
      })
      .subscribe({
        next: () => {
          this.creatingNewLicense = false;
          this.closeCreateNewLicenseDialog();
          this.toastService.addToast(
            'success',
            'New desktop license record created'
          );
          this.loadRegistrationDbRecords();
        },
        error: (err) => {
          this.creatingNewLicense = false;
          this.newLicenseCreateError = this.errorService.getErrorMessage(err);
          this.toastService.addToast('error', this.newLicenseCreateError);
        },
      });
  }
}
